import { ColliClient } from '@frontend/colli/api';
import { Colli, ColliType } from '@frontend/colli/types';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageType } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { Transaction } from '@frontend/transaction/types';
import { MobileWizardStateRecord } from '@frontend/wizard/mobile';

import { PackageLocation, syncWizardTransactionData } from './sync-wizard-transaction-data';

interface PendingPackage {
    type: PackageType;
    workflow_id?: string;
    require_proof_of_identity?: boolean;
}

function buildPudoPackageSequence(workflows: { drop?: string; pick?: string }, proof: { drop?: boolean; pick?: boolean }): PendingPackage[] {
    const sequence: PendingPackage[] = [];

    if (workflows.drop) {
        sequence.push({
            type: PackageType.DROP_OFF,
            workflow_id: workflows.drop,
            require_proof_of_identity: proof.drop ?? false
        });
    }

    if (workflows.pick) {
        sequence.push({
            type: PackageType.PICK_UP,
            workflow_id: workflows.pick,
            require_proof_of_identity: proof.pick ?? false
        });
    }

    return sequence;
}

export function resolvePackageProofFlags(store: MobileWizardStateRecord): { drop: boolean; pick: boolean } {
    const pendingPackages = (store.data.pendingPackages as PendingPackage[] | undefined) || [];
    return {
        drop: store.data.dropOffRequireProofOfIdentity ?? pendingPackages.find((pkg) => pkg.type === PackageType.DROP_OFF)?.require_proof_of_identity ?? false,
        pick: store.data.pickUpRequireProofOfIdentity ?? pendingPackages.find((pkg) => pkg.type === PackageType.PICK_UP)?.require_proof_of_identity ?? false
    };
}

export function resolvePudoPackageSequence(store: MobileWizardStateRecord): PendingPackage[] {
    const dropPkgWf = store.functionalitySettings.find((s) => s.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
    const pickPkgWf = store.functionalitySettings.find((s) => s.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value as string | undefined;
    const proof = resolvePackageProofFlags(store);

    return buildPudoPackageSequence({ drop: dropPkgWf, pick: pickPkgWf }, proof);
}

function idsEqual(left: string | null | undefined, right: string | null | undefined): boolean {
    return (left ?? null) === (right ?? null);
}

function hasAnyLocation(location: PackageLocation): boolean {
    return location.spot_id != null || location.module_id != null || location.slot_id != null;
}

function locationNeedsUpdate(entity: { spot_id?: string | null; module_id?: string | null; slot_id?: string | null }, location: PackageLocation): boolean {
    return !idsEqual(entity.spot_id, location.spot_id) || !idsEqual(entity.module_id, location.module_id) || !idsEqual(entity.slot_id, location.slot_id);
}

/**
 * Patches spot/module/SLOT onto existing collis and packages when location is known.
 * No-op when location is empty so early-created entities keep unset location until a later screen provides it.
 */
export async function syncExistingCollisAndPackagesLocation(
    accountId: string,
    transactionId: string,
    collis: Colli[],
    packages: Package[],
    location: PackageLocation
): Promise<{ collis: Colli[]; packages: Package[] }> {
    if (!hasAnyLocation(location)) {
        return { collis, packages };
    }

    const locationPatch = {
        spot_id: location.spot_id ?? null,
        module_id: location.module_id ?? null,
        slot_id: location.slot_id ?? null
    };

    const updatedCollis: Colli[] = [];
    for (const colli of collis) {
        if (!locationNeedsUpdate(colli, location)) {
            updatedCollis.push(colli);
            continue;
        }

        updatedCollis.push(await ColliClient.patchColli(accountId, transactionId, colli.id, locationPatch));
    }

    const updatedPackages: Package[] = [];
    for (const pkg of packages) {
        if (!locationNeedsUpdate(pkg, location)) {
            updatedPackages.push(pkg);
            continue;
        }

        updatedPackages.push(await PackageClient.patchPackage(accountId, transactionId, pkg.id, locationPatch));
    }

    return { collis: updatedCollis, packages: updatedPackages };
}

/**
 * Patches require_proof_of_identity on existing drop-off / pick-up packages to match wizard store flags.
 */
export async function syncExistingPackagesProof(
    accountId: string,
    transactionId: string,
    packages: Package[],
    proof: { drop: boolean; pick: boolean }
): Promise<Package[]> {
    const updatedPackages: Package[] = [];

    for (const pkg of packages) {
        const desiredProof = pkg.type === PackageType.DROP_OFF ? proof.drop : pkg.type === PackageType.PICK_UP ? proof.pick : undefined;

        if (desiredProof === undefined || pkg.require_proof_of_identity === desiredProof) {
            updatedPackages.push(pkg);
            continue;
        }

        updatedPackages.push(
            await PackageClient.patchPackage(accountId, transactionId, pkg.id, {
                require_proof_of_identity: desiredProof
            })
        );
    }

    return updatedPackages;
}

async function resolveExistingCollis(store: MobileWizardStateRecord, transactionId: string): Promise<Colli[]> {
    if (store.data.collis?.length) {
        return [...store.data.collis];
    }

    return (await ColliClient.fetchCollis({ transaction_id: transactionId })).results;
}

async function resolveExistingPackages(store: MobileWizardStateRecord, transactionId: string): Promise<Package[]> {
    if (store.data.packages?.length) {
        return [...store.data.packages];
    }

    return (await PackageClient.fetchPackages({ transaction_id: transactionId })).results;
}

/**
 * Loads existing collis/packages (store or API) and patches location when known.
 * No-op when nothing exists yet (parcel screen still to come).
 */
export async function syncExistingTransactionCollisAndPackagesLocation(
    store: MobileWizardStateRecord,
    transaction: Transaction,
    location: PackageLocation
): Promise<{ collis: Colli[]; packages: Package[] }> {
    if (!hasAnyLocation(location)) {
        return {
            collis: store.data.collis || [],
            packages: store.data.packages || []
        };
    }

    const collis = await resolveExistingCollis(store, transaction.id);
    const packages = await resolveExistingPackages(store, transaction.id);

    if (collis.length === 0 && packages.length === 0) {
        return { collis, packages };
    }

    return syncExistingCollisAndPackagesLocation(transaction.account_id, transaction.id, collis, packages, location);
}

/**
 * Loads existing packages and patches proof-of-identity flags.
 * No-op when packages do not exist yet.
 */
export async function syncExistingTransactionPackagesProof(
    store: MobileWizardStateRecord,
    transaction: Transaction,
    proof: { drop: boolean; pick: boolean }
): Promise<Package[]> {
    const packages = await resolveExistingPackages(store, transaction.id);
    if (packages.length === 0) {
        return packages;
    }

    return syncExistingPackagesProof(transaction.account_id, transaction.id, packages, proof);
}

/**
 * Creates PUDO packages in order, each depending on the previous package in the chain.
 * Chains are independent per call (e.g. one chain per colli, not across collis).
 */
async function createChainedPudoPackages(
    transaction: Transaction,
    sequence: PendingPackage[],
    location: PackageLocation,
    colliId?: string
): Promise<Package[]> {
    const packages: Package[] = [];
    let previousPackageId: string | undefined;

    for (const pending of sequence) {
        const created = await PackageClient.postPackage(transaction.account_id, transaction.id, {
            type: pending.type,
            workflow_id: pending.workflow_id,
            require_proof_of_identity: pending.require_proof_of_identity,
            user_id: transaction.user_id ?? undefined,
            quantity: 1,
            spot_id: location.spot_id,
            module_id: location.module_id,
            slot_id: location.slot_id,
            blocking: false,
            application: transaction.application,
            feature: transaction.feature,
            colli_id: colliId,
            dependent_package_id: previousPackageId
        });
        packages.push(created);
        previousPackageId = created.id;
    }

    return packages;
}

export async function createPackagesForColli(
    transaction: Transaction,
    colli: Colli,
    location: PackageLocation,
    sequence: PendingPackage[]
): Promise<Package[]> {
    if (sequence.length === 0) return [];
    return createChainedPudoPackages(transaction, sequence, location, colli.id);
}

export async function ensureTransactionCollisAndPackages(
    store: MobileWizardStateRecord,
    transaction: Transaction,
    location: PackageLocation
): Promise<{ collis: Colli[]; packages: Package[]; transaction: Transaction }> {
    const colliWorkflowId = store.functionalitySettings.find((s) => s.key === SettingKey.COLLI_WORKFLOW_ID)?.value;

    const existingPackagesResponse = await PackageClient.fetchPackages({ transaction_id: transaction.id });
    let packages = existingPackagesResponse.results;
    const pendingPackages = (store.data.pendingPackages as PendingPackage[] | undefined) || [];
    const pudoPackageSequence = resolvePudoPackageSequence(store);
    const proofFlags = resolvePackageProofFlags(store);

    if (typeof colliWorkflowId === 'string' && colliWorkflowId.length > 0) {
        const quantity = store.data.quantity || 1;
        const colliType = (store.data.type as ColliType) || ColliType.STANDARD;
        let collis: Colli[] = store.data.collis?.length ? [...store.data.collis] : [];

        if (collis.length === 0) {
            const fetched = await ColliClient.fetchCollis({ transaction_id: transaction.id });
            collis = fetched.results;
        }

        while (collis.length < quantity) {
            const index = collis.length + 1;
            const newColli = await ColliClient.postColli(transaction.account_id, transaction.id, {
                label: `${transaction.tracking_number || transaction.id}-${index}`,
                type: colliType,
                index,
                workflow_id: colliWorkflowId,
                spot_id: location.spot_id,
                module_id: location.module_id,
                slot_id: location.slot_id
            });
            collis.push(newColli);
        }

        for (const colli of collis) {
            const colliPackages = packages.filter((pkg) => pkg.colli_id === colli.id);
            if (colliPackages.length > 0) continue;

            const newPackages = await createPackagesForColli(transaction, colli, location, pudoPackageSequence);
            packages = [...packages, ...newPackages];
        }

        const withLocation = await syncExistingCollisAndPackagesLocation(transaction.account_id, transaction.id, collis, packages, location);
        packages = await syncExistingPackagesProof(transaction.account_id, transaction.id, withLocation.packages, proofFlags);

        return { collis: withLocation.collis, packages, transaction };
    }

    const pendingPackagesToCreate = pendingPackages.length > 0 ? pendingPackages : pudoPackageSequence;
    if (packages.length === 0 && pendingPackagesToCreate.length > 0) {
        const transactionPackages = await createChainedPudoPackages(transaction, pendingPackagesToCreate, location);
        packages = [...packages, ...transactionPackages];
    }

    const withLocation = await syncExistingCollisAndPackagesLocation(transaction.account_id, transaction.id, store.data.collis || [], packages, location);
    packages = await syncExistingPackagesProof(transaction.account_id, transaction.id, withLocation.packages, proofFlags);

    return { collis: withLocation.collis, packages, transaction };
}

export async function syncAndEnsureTransactionCollisAndPackages(store: MobileWizardStateRecord): Promise<{
    collis: Colli[];
    packages: Package[];
    transaction: Transaction;
    senders: Awaited<ReturnType<typeof syncWizardTransactionData>>['senders'];
    receivers: Awaited<ReturnType<typeof syncWizardTransactionData>>['receivers'];
}> {
    const synced = await syncWizardTransactionData(store);
    const ensured = await ensureTransactionCollisAndPackages(store, synced.transaction, synced.location);

    return {
        ...ensured,
        senders: synced.senders,
        receivers: synced.receivers
    };
}
